/**
 * Quiz Play Page (V2 - Polished)
 *
 * Full-screen immersive quiz experience with floating overlays.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MinimalHeader } from "@/components/layout/MinimalHeader";
import { ChoiceScreen } from "@/components/quiz/ChoiceScreen";
import { QuizView } from "@/components/quiz/QuizView";
import { CompletionView } from "@/components/quiz/CompletionView";
import {
  useQuizStore,
  selectMode,
  selectCurrentPrompt,
  selectProgress,
  selectLastAnswer,
  selectAttempts,
  selectFeatures,
  selectConfig,
  selectCurrentIndex,
  selectOrder,
  initializeQuiz,
  startQuiz,
  submitAnswer,
  resetQuiz,
  getQuizStats,
  saveQuizResults,
  chooseExplore,
  chooseQuiz,
} from "@/stores/useQuizStore";
import { fetchGeoJSONFromUrl, getFeatureIdentifier } from "@/utils/geojsonLoader";
import { applyFilters, filterArrayValues } from "@/utils/filterUtils";
import { db } from "@/db/database";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { FilterRule } from "@/stores/useStudioStore";
import { getQuizFlag } from "@/utils/quizMetadata";
import { autoMerge } from "@/utils/grassyJSON";
import { geojsonDatasets } from "@/data/geojsonDatasets";

export function QuizPlayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const renderCount = useRef(0);
  renderCount.current++;

  // Quiz store state
  const mode = useQuizStore(selectMode);
  const config = useQuizStore(selectConfig);
  const currentPrompt = useQuizStore(selectCurrentPrompt);
  const progress = useQuizStore(selectProgress);
  const lastAnswer = useQuizStore(selectLastAnswer);
  const attempts = useQuizStore(selectAttempts);
  const features = useQuizStore(selectFeatures);
  const currentIndex = useQuizStore(selectCurrentIndex);
  const order = useQuizStore(selectOrder);

  // Get quiz signature from URL
  const quizSignature = searchParams.get("signature");

  // Track loading state separately
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);

  // Full quiz config from database (for metadata like flags)
  const [fullQuizConfig, setFullQuizConfig] = useState<import("@/db/types").QuizConfig | null>(null);

  // Label visibility (for both setup and complete modes)
  const [showLabels, setShowLabels] = useState(true);

  // Find dataset for credits display
  const datasetSource = fullQuizConfig?.datasetSource;
  const dataset = datasetSource?.type === 'dataset-url'
    ? geojsonDatasets.find(d => d.path === datasetSource.path)
    : null;

  // Initialize quiz on mount
  useEffect(() => {
    const loadQuiz = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        if (!quizSignature) {
          setLoadError("No quiz selected. Please create a quiz in the Studio.");
          setIsLoading(false);
          return;
        }

        // Load quiz config from database
        const quizConfig = await db.quiz_configs.get(quizSignature);
        if (!quizConfig) {
          setLoadError("Quiz not found. It may have been deleted.");
          setIsLoading(false);
          return;
        }

        // Store full config for metadata functions
        setFullQuizConfig(quizConfig);

        // Load GeoJSON collection
        let collection: FeatureCollection<Geometry, GeoJsonProperties>;

        if (quizConfig.storedCollection) {
          // Inline collection (uploaded file) - already filtered and saved
          collection = JSON.parse(quizConfig.storedCollection);
        } else if (quizConfig.datasetSource?.type === "dataset-url") {
          // Load from URL - need to apply filters
          const rawCollection = await fetchGeoJSONFromUrl(quizConfig.datasetSource.path);

          // Convert StoredFilterRule to FilterRule format (add id field)
          const filters: FilterRule[] = quizConfig.filters.map((f, index) => ({
            ...f,
            id: `filter-${index}`,
          }));

          // Apply filters to features (include/exclude entire features)
          let processedCollection = applyFilters(rawCollection, filters);

          // Apply filters to array values within features (CRITICAL FIX)
          // This ensures only matching array values generate quiz prompts
          // Example: AreaCodes ["404", "621", "583"] with filter "X0X" → only ["404"]
          processedCollection = filterArrayValues(
            processedCollection,
            filters,
            quizConfig.promptKeys
          );

          // Apply exclusions
          // IMPORTANT: Must use original indices from rawCollection for correct feature ID matching
          const excludedSet = new Set(quizConfig.excludedFeatureIds);
          processedCollection = {
            ...processedCollection,
            features: processedCollection.features.filter((feature) => {
              // Find the original index in the raw unfiltered collection
              const originalIndex = rawCollection.features.indexOf(feature);
              if (originalIndex === -1) return true; // Feature not found (shouldn't happen), include it

              const featureId = getFeatureIdentifier(feature, originalIndex);
              return !excludedSet.has(featureId);
            }),
          };

          // Apply auto-merge based on prompt keys
          if (quizConfig.promptKeys.length > 0) {
            const comparisonFunc = (
              a: { properties?: GeoJsonProperties | null },
              b: { properties?: GeoJsonProperties | null }
            ): boolean => {
              if (!a.properties || !b.properties) return false;
              return quizConfig.promptKeys.every((key) => {
                const aValue = a.properties?.[key];
                const bValue = b.properties?.[key];
                const aStr = Array.isArray(aValue)
                  ? JSON.stringify([...aValue].sort())
                  : JSON.stringify(aValue);
                const bStr = Array.isArray(bValue)
                  ? JSON.stringify([...bValue].sort())
                  : JSON.stringify(bValue);
                return aStr === bStr;
              });
            };

            try {
              processedCollection = autoMerge(processedCollection, comparisonFunc, 200);
            } catch (error) {
              console.error("Auto-merge failed in quiz play:", error);
              // Continue with non-merged collection
            }
          }

          collection = processedCollection;
        } else {
          setLoadError("Invalid quiz configuration: missing dataset source");
          setIsLoading(false);
          return;
        }

        // Initialize quiz with config
        // If restarting, skip choice screen and go directly to playing mode
        initializeQuiz(
          {
            category: quizConfig.category,
            geography: quizConfig.geography,
            region: quizConfig.title, // Use title as region name (user-facing quiz name)
            granularityLevel: quizConfig.granularityLevel,
            quizSignature: quizConfig.signature,
            datasetSignature: quizConfig.datasetSignature,
            promptKeys: quizConfig.promptKeys,
            maxAttemptsPerPrompt: quizConfig.maxAttemptsPerPrompt,
            repetitions: quizConfig.repetitions,
          },
          collection,
          isRestarting // Skip choice if restarting (goes to playing mode)
        );

        // Reset restarting flag
        if (isRestarting) {
          setIsRestarting(false);
        }

        // Update lastUsedAt timestamp
        await db.quiz_configs.update(quizSignature, {
          lastUsedAt: new Date().toISOString(),
        });

        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load quiz:", error);
        setLoadError(error instanceof Error ? error.message : "Failed to load quiz");
        setIsLoading(false);
      }
    };

    loadQuiz();
  }, [quizSignature, isRestarting]);

  // Save quiz results when quiz completes
  useEffect(() => {
    if (mode === "complete") {
      const saveResults = async () => {
        try {
          await saveQuizResults();
          console.log("✅ Quiz results saved successfully!");
        } catch (error) {
          console.error("❌ Failed to save quiz results:", error);
        }
      };

      saveResults();
    }
  }, [mode]);

  // Handle feature click - NOT dependent on mode to avoid stale closures in Leaflet
  // The submitAnswer function checks mode internally
  const handleFeatureClick = useCallback((featureId: string) => {
    console.log("handleFeatureClick called:", { featureId });
    submitAnswer(featureId);
  }, []);

  // Handle explore choice from choice screen
  const handleChooseExplore = useCallback(() => {
    chooseExplore();
  }, []);

  // Handle quiz choice from choice screen
  const handleChooseQuiz = useCallback(() => {
    chooseQuiz();
  }, []);

  // Handle start quiz from exploration mode
  const handleStartQuiz = useCallback(() => {
    startQuiz();
  }, []);

  // Handle restart from completion screen (no page reload!)
  const handleRestart = useCallback(() => {
    // Don't use resetQuiz() - it sets mode to "choice" immediately
    // Instead, reinitialize directly with existing config and features
    if (config && features.length > 0) {
      // Build the collection from current features
      const collection = {
        type: "FeatureCollection" as const,
        features: features.map(f => f.feature),
      };

      // Reinitialize with skipChoice=true to go directly to playing mode
      initializeQuiz(config, collection, true);
    }
  }, [config, features]);

  // Handle explore map from completion screen
  const handleExploreMap = useCallback(() => {
    chooseExplore();
  }, []);

  // Handle edit quiz (clone and open in studio)
  const handleEditQuiz = useCallback(async () => {
    if (!quizSignature) return;

    try {
      // Import the cloning utilities dynamically
      const { loadQuizForEditing } = await import("@/utils/quizCloning");
      const { loadConfigForEditing } = await import("@/stores/useStudioStore");

      // Load the quiz config and convert it for editing
      const wizardState = await loadQuizForEditing(quizSignature);

      if (wizardState) {
        // Load into studio store (will clone by default)
        loadConfigForEditing(wizardState, quizSignature, true);

        // Navigate to studio
        navigate("/quiz/studio");
      }
    } catch (error) {
      console.error("Failed to load quiz for editing:", error);
    }
  }, [quizSignature, navigate]);

  // Handle reset
  const handleReset = () => {
    resetQuiz();
    navigate("/");
  };

  // Get stats
  const stats = getQuizStats();

  // Category label
  const categoryLabel =
    config?.category === "phone" ? "Phone Area Codes" :
    config?.category === "admin" ? "Administrative Divisions" :
    config?.category === "cities" ? "Cities" :
    "Custom Quiz";

  console.log(`🎮 QuizPlayPage render #${renderCount.current}:`, {
    mode,
    isLoading,
    loadError,
    featuresCount: features.length,
    showLabels,
    featuresReference: features,
  });

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Minimal Header (immersive mode) */}
      <MinimalHeader
        title={fullQuizConfig ? `${getQuizFlag(fullQuizConfig)} ${fullQuizConfig.title}` : "Quiz"}
        subtitle={`${categoryLabel}${config?.granularityLevel ? ` • Level ${config.granularityLevel}` : ""}`}
        showProgress={mode === "playing"}
        progress={progress}
        actions={
          <Button variant="outline" size="sm" onClick={handleReset}>
            Exit Quiz
          </Button>
        }
      />

      {/* Main content - full screen map */}
      <div className="relative flex-1 h-full">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-modal pointer-events-auto">
            <Card className="max-w-md w-full mx-4">
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-semibold mb-4">Loading quiz...</h2>
                <p className="text-muted-foreground">Please wait</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error State */}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-modal pointer-events-auto">
            <Card className="max-w-md w-full mx-4">
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-semibold mb-4 text-destructive">Error Loading Quiz</h2>
                <p className="text-muted-foreground mb-4">{loadError}</p>
                <Button onClick={() => navigate("/")}>Go Home</Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quiz Map - Render for all non-loading states (choice, setup, playing, complete) */}
        {!isLoading && !loadError && features.length > 0 && (
          <QuizView
            features={features}
            config={config}
            currentPrompt={currentPrompt}
            currentIndex={currentIndex}
            order={order}
            progress={progress}
            lastAnswer={lastAnswer}
            attempts={attempts}
            dataset={dataset || undefined}
            onFeatureClick={handleFeatureClick}
            showCompletionLabels={showLabels}
          />
        )}

        {/* Setup Mode - Floating "Start Quiz" Button */}
        {!isLoading && !loadError && mode === "setup" && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-dropdown w-full max-w-4xl px-4 pointer-events-none">
            <Card className="bg-card/95 backdrop-blur-md shadow-xl transition-all duration-300 pointer-events-auto">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">Exploration Mode</h2>
                    <p className="text-sm text-muted-foreground">
                      Study the map and familiarize yourself with the geography.
                      Hover over regions to see details.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant={showLabels ? "default" : "outline"}
                      onClick={() => setShowLabels(!showLabels)}
                      className="h-9"
                    >
                      {showLabels ? "👁️ Hide" : "👁️ Show"} Labels
                    </Button>
                    <Button size="lg" onClick={handleStartQuiz}>
                      ▶️ Start Quiz
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Choice Screen (overlay on map) */}
        {!isLoading && !loadError && mode === "choice" && (
          <ChoiceScreen
            quizTitle={fullQuizConfig?.title || "Quiz"}
            onExplore={handleChooseExplore}
            onStartQuiz={handleChooseQuiz}
          />
        )}

        {/* Completion Mode */}
        {!isLoading && !loadError && mode === "complete" && (
          <CompletionView
            stats={stats}
            onRestart={handleRestart}
            onExploreMap={handleExploreMap}
            onExit={handleReset}
            onViewKnowledge={() => navigate("/knowledge")}
            onEditQuiz={handleEditQuiz}
            showLabels={showLabels}
            onToggleLabels={() => setShowLabels(!showLabels)}
          />
        )}
      </div>
    </div>
  );
}
